import React from 'react';
import { Link } from 'react-router-dom';

const Button = ({
  variant = 'primary',
  size = 'medium',
  children,
  className = '',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  href,
  to,
  fullWidth = false,
  // A bare <button> inside a form is a submit button per the HTML spec, which
  // made Cancel buttons save the form they were meant to abandon. Every button
  // that really submits passes type="submit" itself.
  type = 'button',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed';

  // Variant classes - colour, border, radius and shadow only. Padding and text
  // size are owned by sizeClasses below so that every variant honours `size`.
  // The small variants keep their tighter radius and flatter shadow, which is a
  // shape difference rather than a spacing one.
  const getVariantClasses = (variant, size) => {
    const isSmall = size === 'small';
    if (variant === 'primary') {
      const shape = isSmall
        ? 'rounded-lg shadow-sm hover:shadow'
        : 'rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5';
      return `bg-primary-500 hover:bg-primary-600 active:scale-[0.98] text-white font-medium ${shape} transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
    }
    if (variant === 'secondary') {
      const shape = isSmall
        ? 'rounded-lg shadow-sm'
        : 'rounded-xl shadow-sm hover:shadow';
      return `bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 border border-slate-200 font-medium ${shape} transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-200`;
    }
    if (variant === 'accent') {
      const shape = isSmall
        ? 'rounded-lg shadow-sm'
        : 'rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5';
      return `bg-accent-500 hover:bg-accent-600 active:scale-[0.98] text-white font-medium ${shape} transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-500/50`;
    }

    const baseVariants = {
      danger: 'bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white rounded-xl shadow-sm transition-all focus:ring-red-500/50',
      ghost: 'bg-transparent hover:bg-slate-100/80 active:scale-[0.98] text-slate-600 hover:text-slate-900 rounded-lg transition-all focus:ring-slate-200',
      outline: 'bg-transparent border-2 border-primary-500 active:scale-[0.98] text-primary-600 hover:bg-primary-50 rounded-xl transition-all focus:ring-primary-500/50'
    };
    // baseVariants holds no primary key, so fall back through the primary
    // branch above rather than to an undefined lookup. Going via the function
    // keeps the size-dependent shape that primary applies.
    return baseVariants[variant] ?? getVariantClasses('primary', size);
  };

  // The single owner of padding and text size, applied to every variant. An
  // unrecognised size falls back to medium rather than leaving the button with
  // no padding at all.
  const sizeClasses = {
    small: 'py-1.5 px-3 text-sm',
    medium: 'py-2 px-4',
    large: 'py-2.5 px-5 text-base'
  };

  const variantClass = getVariantClasses(variant, size);
  const sizeClass = sizeClasses[size] || sizeClasses.medium;

  const fullWidthClass = fullWidth ? 'w-full' : '';

  const classes = `${baseClasses} ${variantClass} ${sizeClass} ${fullWidthClass} ${className}`;

  const isDisabled = disabled || loading;

  // Loading spinner
  const LoadingSpinner = () => (
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
  );

  // Icon rendering
  const renderIcon = () => {
    if (!icon) return null;
    return React.cloneElement(icon, {
      className: `h-4 w-4 ${iconPosition === 'left' ? 'mr-2' : 'ml-2'}`
    });
  };

  // Content with loading state
  const renderContent = () => (
    <>
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === 'left' && renderIcon()}
      {children}
      {!loading && icon && iconPosition === 'right' && renderIcon()}
    </>
  );

  // A link Button cannot rely on the `disabled` attribute, which anchors ignore,
  // nor on the `disabled:` class variants, which only match a real disabled
  // control. Dropping the navigation target is what actually makes it inert;
  // the rest keeps it out of the tab order and matches the disabled look.
  if ((href || to) && isDisabled) {
    return (
      <a
        className={`${classes} opacity-50 cursor-not-allowed pointer-events-none`}
        aria-disabled="true"
        tabIndex={-1}
        {...props}
      >
        {renderContent()}
      </a>
    );
  }

  // External link
  if (href) {
    return (
      <a
        href={href}
        className={classes}
        {...props}
      >
        {renderContent()}
      </a>
    );
  }

  // React Router Link
  if (to) {
    return (
      <Link
        to={to}
        className={classes}
        {...props}
      >
        {renderContent()}
      </Link>
    );
  }

  // Regular button
  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;